import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, FileText, TrendingUp, CheckCircle, XCircle, AlertCircle, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Billing = () => {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');

  useEffect(() => {
    fetchSubscription();
    fetchInvoices();
    fetchUsage();
    fetchPaymentMethods();
  }, []);

  const fetchSubscription = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/billing/subscriptions/current`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data.subscription);
      } else {
        setError('Failed to fetch subscription');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  };

  const fetchUsage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/usage/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentMethods(data);
      }
    } catch (err) {
      console.error('Failed to fetch payment methods:', err);
    }
  };

  const handleUpgrade = async (tier) => {
    try {
      const res = await fetch(`${API_URL}/api/billing/subscriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier })
      });
      if (res.ok) {
        await fetchSubscription();
        setShowUpgrade(false);
      } else {
        setError('Failed to upgrade subscription');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // S1 self-serve payment: create a Stripe-hosted Checkout session for the £49
  // SMB tier and redirect the browser to it. Distinct from handleUpgrade (which
  // mutates the subscription in-app) — card entry happens on Stripe, not here.
  const handleCheckout = async () => {
    setCheckoutBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/billing/checkout-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(orgId ? { 'X-Organization-Id': orgId } : {}),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tier: 'smb' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url; // hand off to Stripe-hosted checkout
        return;
      }
      // Fail-closed endpoint returns 503 with a detail when Stripe isn't configured.
      setError(data.detail || 'Checkout is unavailable right now. Please try again later.');
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckoutBusy(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getTierFeatures = (tier) => {
    const features = {
      starter: ['DirectConnect', 'Up to 2 locations', '5 users', '1,000 WhatsApp messages/month'],
      growth: ['DirectConnect', 'VoiceConcierge', 'InventoryAI', 'Up to 10 locations', '20 users', '10,000 WhatsApp messages/month'],
      scale: ['All features', 'Unlimited locations', 'Unlimited users', 'Unlimited messages', 'OpenPOS', 'Alerting System']
    };
    return features[tier] || [];
  };

  const getTierPrice = (tier) => {
    const prices = {
      starter: 99,
      growth: 299,
      scale: 799
    };
    return prices[tier] || 0;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Subscription</h1>
        <p className="text-gray-600">Manage your subscription and payment methods</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* No active subscription → self-serve S1 checkout (the state a new customer is in). */}
      {!loading && !subscription && (
        <div className="bg-white shadow rounded-lg p-6 mb-6" data-testid="billing-no-subscription">
          <h2 className="text-xl font-semibold mb-2">Start your subscription</h2>
          <p className="text-gray-600 mb-4">
            You don&apos;t have an active plan yet. Subscribe to unlock cost intelligence and the secure document vault.
          </p>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-bold text-gray-900">£49</span>
            <span className="text-gray-500">/month</span>
          </div>
          <button
            onClick={handleCheckout}
            disabled={checkoutBusy}
            data-testid="billing-checkout-smb"
            className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
          >
            {checkoutBusy ? 'Redirecting…' : 'Subscribe — £49/mo (Stripe)'}
          </button>
        </div>
      )}

      {subscription && (
        <>
          {/* Current Subscription */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold mb-2">Current Plan</h2>
                <div className="flex items-center space-x-4">
                  <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium capitalize">
                    {subscription.tier}
                  </span>
                  <span className={`px-4 py-2 rounded-lg font-medium capitalize ${
                    subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                    subscription.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {subscription.status}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(subscription.monthly_price)}</p>
                <p className="text-sm text-gray-500">per month</p>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Features Included:</h3>
              <div className="grid grid-cols-2 gap-2">
                {getTierFeatures(subscription.tier).map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {subscription.trial_end && new Date(subscription.trial_end) > new Date() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  Trial ends: {new Date(subscription.trial_end).toLocaleDateString()}
                </p>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => setShowUpgrade(!showUpgrade)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {subscription.tier === 'scale' ? 'Current Plan' : 'Upgrade Plan'}
              </button>
              {subscription.status === 'active' && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to cancel your subscription?')) {
                      // TODO: Implement cancellation
                      alert('Cancellation feature coming soon');
                    }
                  }}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>

          {/* Upgrade Options */}
          {showUpgrade && subscription.tier !== 'scale' && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Upgrade Your Plan</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['starter', 'growth', 'scale'].filter(t => t !== subscription.tier).map((tier) => (
                  <div key={tier} className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 transition">
                    <h3 className="font-semibold capitalize mb-2">{tier}</h3>
                    <p className="text-2xl font-bold mb-4">{formatCurrency(getTierPrice(tier))}/month</p>
                    <ul className="text-sm text-gray-600 mb-4 space-y-1">
                      {getTierFeatures(tier).slice(0, 3).map((feature, idx) => (
                        <li key={idx}>• {feature}</li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handleUpgrade(tier)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Upgrade to {tier}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Usage Summary */}
          {usage && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Usage This Month</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(usage.usage || {}).map(([metric, data]) => (
                  <div key={metric} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {metric.replace(/_/g, ' ')}
                      </span>
                      {data.unlimited ? (
                        <span className="text-sm text-gray-500">Unlimited</span>
                      ) : (
                        <span className="text-sm text-gray-500">
                          {data.used} / {data.limit}
                        </span>
                      )}
                    </div>
                    {!data.unlimited && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            data.percentage > 80 ? 'bg-red-500' :
                            data.percentage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(data.percentage, 100)}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Methods */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Payment Methods</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Payment Method</span>
              </button>
            </div>
            {paymentMethods.length > 0 ? (
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <CreditCard className="w-6 h-6 text-gray-400" />
                      <div>
                        <p className="font-medium">
                          {method.brand ? method.brand.toUpperCase() : 'Card'} •••• {method.last4 || '****'}
                        </p>
                        {method.expiry_month && method.expiry_year && (
                          <p className="text-sm text-gray-500">
                            Expires {method.expiry_month}/{method.expiry_year}
                          </p>
                        )}
                      </div>
                    </div>
                    {method.is_default && (
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        Default
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No payment methods added yet</p>
            )}
          </div>

          {/* Invoices */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Invoices</h2>
            {invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoice_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(invoice.total_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            invoice.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {invoice.invoice_pdf_url && (
                            <a
                              href={invoice.invoice_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Download
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No invoices yet</p>
            )}
          </div>
        </>
      )}

      {!subscription && !loading && (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Active Subscription</h3>
          <p className="text-gray-500 mb-6">Get started by selecting a plan</p>
          <button
            onClick={() => handleUpgrade('starter')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Start Free Trial
          </button>
        </div>
      )}
    </div>
  );
};

export default Billing;
